import { GetTeamResponse, UpdateTeamRequest } from '@/api';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useOpenApi } from '@/common/providers/open-api-provider';
import { getFallbackErrorKey, isApiError } from '@/common/util';
import { UIToast } from '@/ui';
import { useCompaniesTranslation, useTeamPermissions } from '../hooks';
import { addOrUpdateAllTeamsCache } from '../queries/company-cache';

type UseUpdateTeamActionProps = {
    teamId: string;
    /**
     * What to say when the save succeeds, as a whole sentence. One endpoint
     * backs every section of the team settings page; today they all mean the
     * same thing by it, so this is only worth passing where a section wants to
     * be more specific than "Team updated".
     *
     * A sentence rather than a noun to slot into a template, because Spanish
     * agrees the participle with it: *notificaciones actualizadas* but *modo
     * actualizado*.
     */
    successMessage?: string;
};

type UseUpdateTeamActionData = {
    /** Whether this viewer can change this team's settings right now. */
    isAllowed: boolean;
    /** Why not, ready to show in a tooltip. Empty while allowed. */
    reason: string;
    updateTeam: (data: UpdateTeamRequest) => Promise<GetTeamResponse>;
    loading: boolean;
    error: string;
};

/**
 * Changing a team's settings: whether this viewer may do it, why not when they
 * may not, and the call itself.
 *
 * Raises both toasts itself. On failure the reason is the information — the
 * caller pressing Save already knows what it was saving — so the specific
 * refusal is shown. On success the reason is nothing and what was saved is
 * everything, which is what `successMessage` is for.
 */
export function useUpdateTeamAction(
    props: UseUpdateTeamActionProps
): UseUpdateTeamActionData {
    const { teamId, successMessage } = props;

    const { t } = useCompaniesTranslation();
    const { openApi } = useOpenApi();
    const openApiRef = useRef(openApi);
    useEffect(() => {
        openApiRef.current = openApi;
    });

    const queryClient = useQueryClient();

    const { canEditTeam } = useTeamPermissions({ teamId });

    const [isAllowed, reason] = useMemo(() => {
        if (!canEditTeam) {
            return [false, t('actions.update_team.reason_not_permitted')];
        }

        return [true, ''];
    }, [canEditTeam, t]);

    // One place that turns a failure into a message, so the toast and the
    // returned `error` can never disagree.
    //
    // Every error `PATCH /v3/teams/{id}` declares gets a branch — the contact
    // ones matter most, since they are the refusals a user can act on.
    const getErrorMessage = useCallback(
        (error: unknown): string => {
            if (isApiError(error, 'CANNOT_ENABLE_ARC_WITHOUT_CONTACTS')) {
                return t('actions.update_team.error_arc_without_contacts');
            }
            if (isApiError(error, 'CANNOT_REMOVE_CONTACTS_WITH_ARC_ENABLED')) {
                return t('actions.update_team.error_contacts_with_arc');
            }
            if (
                isApiError(error, 'CANNOT_ENABLE_OUT_OF_HOURS_WITHOUT_CONTACTS')
            ) {
                return t(
                    'actions.update_team.error_out_of_hours_without_contacts'
                );
            }
            if (
                isApiError(
                    error,
                    'CANNOT_REMOVE_OUT_OF_HOURS_CONTACTS_WHEN_ENABLED'
                )
            ) {
                return t(
                    'actions.update_team.error_contacts_with_out_of_hours'
                );
            }
            // Both mean the same to the user: not a team they may change.
            if (
                isApiError(error, 'CANNOT_UPDATE_TEAM') ||
                isApiError(error, 'TEAM_NOT_FOUND')
            ) {
                return t('shared.errors.unauthorised');
            }
            if (isApiError(error, 'BAD_REQUEST')) {
                return error.response?.data.message.join(', ') ?? '';
            }
            // `InvalidTaskTimeSettingsErrorResponse` is a 400 carrying a
            // server-written sentence rather than a code, so there is nothing
            // for `isApiError` to match — it arrives here and is shown as-is.
            const message = axios.isAxiosError(error)
                ? (error.response?.data as { message?: unknown } | undefined)
                      ?.message
                : undefined;
            if (typeof message === 'string') {
                return message;
            }

            return t(getFallbackErrorKey(error));
        },
        [t]
    );

    const mutation = useMutation({
        mutationFn: (data: UpdateTeamRequest) =>
            openApiRef.current.teams
                .updateTeam(teamId, data)
                .then((response) => response.data),
        onSuccess: (data) => {
            addOrUpdateAllTeamsCache(queryClient, data.data.team);

            UIToast.success(successMessage ?? t('shared.teams.toast_updated'));
        },
        onError: (error) => {
            UIToast.error(getErrorMessage(error));
        }
    });

    return {
        isAllowed,
        reason,
        updateTeam: (data: UpdateTeamRequest): Promise<GetTeamResponse> =>
            mutation.mutateAsync(data),
        loading: mutation.isPending,
        error: mutation.error ? getErrorMessage(mutation.error) : ''
    };
}
